import cn from "classnames";
import { useState, ChangeEvent, MouseEvent, DragEvent } from "react";
import { AttachmentDocumentFloating as AttachmentDocumentFloatingImage } from "@rshbintech.dboul/images";
import { A11yRoot, A11yChild } from "../a11y";
import { A11Y_CHILD_MIN_INDEX, useA11yNestedNavigation } from "../a11y/hooks";
import { Text } from "../text";
import { Button } from "../button";
import { FileItem } from "./files-list/file-item";
import { FilesList } from "./files-list";
import {
  validateFiles,
  downloadFileFromUI,
  isFileAlreadyAdded,
  findAttachmentById,
  filterAttachmentsById,
  createRestrictionText,
  encodeFilesToAttachments,
} from "./utils";
import { DropzoneProps, Attachment } from "./types";
import { useUploadInput } from "./hooks";
import styles from "./styles.module.scss";

export const Dropzone = ({
  children,
  className,
  maxFileSize,
  fileItemComponent,
  multiple = true,
  attachments = [],
  acceptedFileTypes = [],
  dataTestId = "dropzone",
  onChange,
  onDeleteFileFromServer,
  onDownloadFileFromServer,
}: DropzoneProps) => {
  const [isDragActive, setIsDragActive] = useState(false);

  const uploadButtonIndex = attachments.length * 2;
  const deleteAllButtonIndex = attachments.length * 2 + 1;
  const showDeleteAllButton = attachments.length > 1;

  const {
    isA11yChildActive,
    a11yCurrentChildIndex,
    handleEnter,
    handleArrowUp,
    handleArrowDown,
    handleAfterBlur,
    setA11yChildRefs,
    setA11yCurrentChildIndex,
  } = useA11yNestedNavigation({ maxIndex: uploadButtonIndex });

  const { uploadInputRef, handleInputKeydown, handleUploadButtonClick } =
    useUploadInput();

  const getNewAttachmentsState = async (
    filesFromEvent: FileList
  ): Promise<undefined | Attachment[]> => {
    const filesFromList = Array.from(filesFromEvent);

    const newFiles = filesFromList.filter(
      (file) => !isFileAlreadyAdded(file, attachments)
    );

    if (newFiles.length === 0) {
      return;
    }

    const validatedNewFiles = validateFiles(newFiles, {
      maxFileSize,
      acceptedFileTypes,
    });

    try {
      const attachmentsFromNewFiles = await encodeFilesToAttachments(
        validatedNewFiles
      );

      return [...attachments, ...attachmentsFromNewFiles];
    } catch (error) {
      return attachments;
    }
  };

  const handleDrop = async (event: DragEvent<HTMLDivElement>) => {
    // Запретить переход на содержимое файла
    event.preventDefault();

    setIsDragActive(false);

    if (event.dataTransfer) {
      const newAttachmentsState = await getNewAttachmentsState(
        event.dataTransfer.files
      );

      if (!newAttachmentsState) {
        return;
      }

      onChange?.(event, newAttachmentsState);
    }
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    //Запретить переход на содержимое файла
    event.preventDefault();
  };

  const handleDragEnter = (event: MouseEvent) => {
    // Условие срабатывает при перетаскивании файла на вложенный элемент дропзоны чтобы не терялось выделение
    if (event.currentTarget.contains(event.relatedTarget as Node)) {
      return;
    }

    setIsDragActive(true);
  };

  const handleDragLeave = (event: MouseEvent) => {
    // Условие срабатывает при перетаскивании файла на вложенный элемент дропзоны чтобы не терялось выделение
    if (event.currentTarget.contains(event.relatedTarget as Node)) {
      return;
    }

    setIsDragActive(false);
  };

  // Повторное добавление файла не вызывает событие onChange, поэтому при клике очищаем значение поля
  const handleClick = (event: MouseEvent<HTMLInputElement>) => {
    event.currentTarget.value = "";
  };

  const handleInputChange = async (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const newAttachmentsState = await getNewAttachmentsState(
        event.target.files
      );

      if (!newAttachmentsState) {
        return;
      }

      onChange?.(event, newAttachmentsState);
      setA11yCurrentChildIndex(
        isA11yChildActive ? newAttachmentsState.length - 1 : -1
      );
    }
  };

  const handleFileDelete = (
    event: MouseEvent | KeyboardEvent,
    fileId: string | number
  ) => {
    const attachmentToDelete = findAttachmentById(attachments, fileId);
    const filteredAttachments = filterAttachmentsById(attachments, fileId);

    if (!attachmentToDelete?.type) {
      onDeleteFileFromServer?.(event, fileId);
    }

    onChange?.(event, filteredAttachments);
    setA11yCurrentChildIndex(isA11yChildActive ? A11Y_CHILD_MIN_INDEX : -1);
  };

  const handleDeleteAll = (event: MouseEvent | KeyboardEvent) => {
    attachments.forEach((attachment) => {
      if (!attachment.type) {
        onDeleteFileFromServer?.(event, attachment.id);
      }
    });

    onChange?.(event, []);
    setA11yCurrentChildIndex(isA11yChildActive ? A11Y_CHILD_MIN_INDEX : -1);
  };

  const handleFileDownload = (
    event: MouseEvent | KeyboardEvent,
    fileId: number | string
  ) => {
    const attachment = findAttachmentById(attachments, fileId);

    if (attachment) {
      // Если присутствует ссылка на файл, то скачать файл можно без обращения к серверу.
      if ("fileSource" in attachment) {
        downloadFileFromUI(attachment);
      } else {
        onDownloadFileFromServer?.(event, attachment.id);
      }
    }
  };

  const dropzoneRestrictionText = createRestrictionText(
    maxFileSize,
    acceptedFileTypes
  );

  const dropzoneRestrictionTextComponent = dropzoneRestrictionText ? (
    <Text
      className={styles["dropzone-restriction-text"]}
      dataTestId={`${dataTestId}-restriction-text`}
    >
      {dropzoneRestrictionText}
    </Text>
  ) : null;

  return (
    <A11yRoot
      onEnter={handleEnter}
      onArrowUp={handleArrowUp}
      onArrowDown={handleArrowDown}
      onAfterBlur={handleAfterBlur}
    >
      <div
        data-test-id={`${dataTestId}-container`}
        className={cn(styles.container, className)}
      >
        {children}
        <div
          className={cn(styles["dropzone-area"], {
            [styles["drag-active"]]: isDragActive,
          })}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          data-test-id={`${dataTestId}-area`}
        >
          <input
            tabIndex={-1}
            ref={uploadInputRef}
            type="file"
            multiple={multiple}
            onChange={handleInputChange}
            onClick={handleClick}
            className={styles["file-input"]}
            accept={acceptedFileTypes.toString()}
            data-test-id={`${dataTestId}-file-input`}
            onKeyDown={handleInputKeydown}
          />
          <AttachmentDocumentFloatingImage
            className={styles["move-file-icon"]}
          />
          <Text className={styles["dropzone-hint-text"]}>
            Переместите файлы или&nbsp;
            <A11yChild
              hasA11yFocus={a11yCurrentChildIndex === uploadButtonIndex}
              a11yChildRef={setA11yChildRefs(uploadButtonIndex)}
            >
              <button
                type="button"
                className={styles["file-opener-link"]}
                data-test-id={`${dataTestId}-file-opener-link`}
                onClick={handleUploadButtonClick}
              >
                выберите в папке
              </button>
            </A11yChild>
          </Text>
          {dropzoneRestrictionTextComponent}
        </div>
        {attachments.length > 0 && (
          <div
            className={cn(styles["attachments-container"], {
              [styles["drag-active"]]: isDragActive,
            })}
          >
            <FilesList
              attachments={attachments}
              fileItemComponent={fileItemComponent}
              onFileDelete={handleFileDelete}
              onFileDownload={handleFileDownload}
              dataTestId={`${dataTestId}-files-list`}
              a11yChildRefWithIndex={setA11yChildRefs}
              a11yChildCurrentIndex={a11yCurrentChildIndex}
            />
          </div>
        )}
        {showDeleteAllButton && (
          <A11yChild
            hasA11yFocus={a11yCurrentChildIndex === deleteAllButtonIndex}
            a11yChildRef={setA11yChildRefs(deleteAllButtonIndex)}
          >
            <div className={cn(styles["delete-button-container"])}>
              <Button
                type="link-danger"
                dataTestId={`${dataTestId}-delete-all-button`}
                onClick={handleDeleteAll}
              >
                Удалить все
              </Button>
            </div>
          </A11yChild>
        )}
      </div>
    </A11yRoot>
  );
};

Dropzone.FileItem = FileItem;
